import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, ChevronDown } from "lucide-react";
import { IMG, waLink } from "@/lib/site";
import { MaskLine } from "@/components/Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.14]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      id="start"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden grain bg-black"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
        <motion.img
          src={IMG.heroVan}
          alt="Transporter von Global Move Europa in europäischer Stadt"
          initial={{ scale: 1.14, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full object-cover object-center"
        />
      </motion.div>

      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/78 to-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-black/60" />
      <motion.div
        aria-hidden
        initial={{ x: "-120%" }}
        animate={{ x: "140%" }}
        transition={{ duration: 2.6, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-[-12deg] pointer-events-none"
      />

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 min-h-[100svh] flex flex-col justify-end pb-12 sm:pb-16 pt-28 px-5 sm:px-8 lg:px-12"
      >
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mb-7 sm:mb-9"
        >
          <div className="flex items-center gap-3">
            <span className="hazard-stripe w-12 sm:w-16 h-[9px]" />
            <span className="font-display text-base sm:text-xl font-bold uppercase tracking-[0.16em] text-white">
              Global Move Europa
            </span>
          </div>
          <div className="eyebrow text-[#ff7a2f] mt-2 ml-[60px] sm:ml-[76px]">
            Transport · Umzug · Logistik
          </div>
        </motion.div>

        <h1 className="font-display uppercase font-black leading-[0.86] tracking-[-0.015em] text-white text-[clamp(2.7rem,10.5vw,8.4rem)] max-w-[1500px]">
          <MaskLine delay={0.35} duration={1.15}>
            Wir bringen es
          </MaskLine>
          <MaskLine delay={0.5} duration={1.15}>
            <span className="text-[#f95700]">sicher ans Ziel.</span>
          </MaskLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95 }}
          className="mt-6 sm:mt-8 max-w-2xl text-[15px] sm:text-lg text-neutral-300 leading-relaxed"
        >
          Professionelle Transporte und Umzüge – zuverlässig in Bremen, deutschlandweit und
          europaweit.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1 }}
          className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-stretch w-full sm:w-auto"
        >
          <a
            href="#konfigurator"
            data-testid="hero-quote-button"
            className="group inline-flex items-center justify-between gap-5 bg-[#f95700] hover:bg-[#e04e00] text-white h-16 px-6 sm:pl-8 sm:pr-6 font-display text-lg sm:text-xl font-bold uppercase tracking-wide transition-colors clip-notch"
          >
            Transport anfragen
            <ArrowRight size={22} className="transition-transform duration-500 group-hover:translate-x-1.5" />
          </a>
          <a
            href={waLink("Hallo Global Move Europa, ich möchte schnell per WhatsApp einen Transport anfragen.")}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-button"
            className="group inline-flex items-center justify-between gap-5 border border-white/30 hover:border-white bg-white/5 backdrop-blur-md text-white h-16 px-6 sm:pl-8 sm:pr-6 font-display text-lg sm:text-xl font-bold uppercase tracking-wide transition-colors"
          >
            <span className="flex flex-col items-start leading-tight text-left">
              WhatsApp kontaktieren
              <span className="font-mono-t text-[10px] normal-case tracking-normal text-neutral-400">
                Schnell per WhatsApp anfragen
              </span>
            </span>
            <MessageCircle size={22} className="text-[#25D366] shrink-0 transition-transform duration-500 group-hover:scale-110" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          className="mt-9 sm:mt-12 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-white/15 pt-5"
        >
          {["Privat", "Gewerbe", "Umzüge", "Transporte"].map((t, i) => (
            <span key={t} className="flex items-center gap-3">
              {i > 0 && <span className="w-1.5 h-1.5 bg-[#f95700] rotate-45" />}
              <span className="font-mono-t text-[11px] sm:text-xs uppercase tracking-[0.2em] text-neutral-300">
                {t}
              </span>
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-5 right-5 sm:right-8 z-10 hidden sm:flex flex-col items-center gap-2"
      >
        <span className="font-mono-t text-[10px] uppercase tracking-[0.25em] text-neutral-500 [writing-mode:vertical-rl]">
          Scrollen
        </span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ChevronDown size={18} className="text-[#f95700]" />
        </motion.span>
      </motion.div>
    </section>
  );
};
